import { defineStore } from 'pinia'
import { ref } from 'vue'

/**
 * Recent pages, kept in this browser only.
 *
 * Nothing is sent per page view, so it costs no request. One entry per path - changing a
 * page's filters updates its entry (and the URL it reopens with) rather than adding another.
 * Keyed by user, so a shared browser never shows one person's history to the next, and
 * cleared on logout. Storage can refuse (private windows, blocked site data); then there is
 * simply no history.
 *
 * The menu shows five (Kenneth: eight was too many beside a long bookmark list). One more is
 * kept because the page on screen is always in the list and never shown.
 */
export const SHOWN = 5
const KEEP = SHOWN + 1
const keyFor = (userId) => `cortex.recent.${userId}`

function read(userId) {
    try {
        const raw = localStorage.getItem(keyFor(userId))
        const list = raw ? JSON.parse(raw) : []
        return Array.isArray(list) ? list.slice(0, KEEP) : []
    } catch {
        return []
    }
}

function write(userId, list) {
    try {
        localStorage.setItem(keyFor(userId), JSON.stringify(list))
    } catch {
        // No history then.
    }
}

export const useRecentPagesStore = defineStore('recentPages', () => {
    const entries = ref([])
    let userId = null

    function bind(id) {
        userId = id ?? null
        entries.value = userId ? read(userId) : []
    }

    function record({ path, url, label, context = null }) {
        if (!userId || !path) return
        const rest = entries.value.filter(e => e.path !== path)
        entries.value = [{ path, url: url || path, label, context }, ...rest].slice(0, KEEP)
        write(userId, entries.value)
    }

    function clear() {
        if (userId) {
            try {
                localStorage.removeItem(keyFor(userId))
            } catch {
                // Nothing to clear.
            }
        }
        entries.value = []
    }

    return { entries, bind, record, clear }
})
